import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, AlertCircle, Copy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LoginResponse } from '../types/Auth';
import QRCode from 'qrcode';

interface MfaSetupProps {
  onComplete?: () => void;
  onCancel?: () => void;
  email?: string; // Allow email to be passed in for login flow
}

const MfaSetup: React.FC<MfaSetupProps> = ({ onComplete, onCancel, email: propEmail }) => {
  const { user, setupMfa, verifyMfaSetup } = useAuth();
  const email = propEmail || user?.email || '';
  const [step, setStep] = useState<'setup' | 'verify'>('setup');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [manualEntryKey, setManualEntryKey] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const initializedEmailRef = useRef<string | null>(null);

  useEffect(() => {
    console.log('MfaSetup useEffect - step:', step, 'email:', email, 'initializedEmail:', initializedEmailRef.current);
    // Only initialize if we're in setup step, have email, and haven't already initialized for this email
    if (step === 'setup' && email && initializedEmailRef.current !== email && !isLoading) {
      console.log('Initializing MFA setup for email:', email);
      initializedEmailRef.current = email;
      initializeSetup();
    } else if (step === 'setup' && !email) {
      console.warn('MfaSetup: Email is missing, cannot initialize setup');
      setError('Email is required for MFA setup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, email]);

  const initializeSetup = async () => {
    if (!email) {
      setError('Email is required for MFA setup');
      return;
    }

    console.log('initializeSetup called for email:', email);
    setIsLoading(true);
    setError(null);

    try {
      console.log('Calling setupMfa API...');
      const response = await setupMfa(email);
      console.log('setupMfa response:', response);
      console.log('QR Code URL:', response.qrCodeUrl);
      console.log('Manual Entry Key:', response.manualEntryKey);
      
      if (!response.qrCodeUrl) {
        throw new Error('QR code URL not provided in response');
      }
      
      if (!response.manualEntryKey) {
        throw new Error('Manual entry key not provided in response');
      }
      
      setQrCodeUrl(response.qrCodeUrl);
      setManualEntryKey(response.manualEntryKey);

      // Generate QR code image
      console.log('Generating QR code data URL from:', response.qrCodeUrl);
      try {
        const dataUrl = await QRCode.toDataURL(response.qrCodeUrl);
        console.log('QR code generated successfully, length:', dataUrl.length);
        setQrCodeDataUrl(dataUrl);
      } catch (qrError: any) {
        console.error('Error generating QR code image:', qrError);
        // Don't fail the whole setup if QR code generation fails
        // The manual entry key is still available
        setError('QR code image could not be generated, but you can use the manual entry key below');
      }
    } catch (err: any) {
      console.error('Error initializing MFA setup:', err);
      console.error('Error details:', err.response || err.message);
      setError(err.message || 'Failed to initialize MFA setup. Please try again.');
    } finally {
      console.log('initializeSetup completed, setting isLoading to false');
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user && !email) {
      setError('Email is required for MFA setup');
      return;
    }

    const userEmail = user?.email || email;
    if (!userEmail) {
      setError('Email is required for MFA setup');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await verifyMfaSetup(userEmail, verificationCode) as LoginResponse;
      // Backend automatically logs in after MFA setup
      if (response.token) {
        setSuccess(true);
        setTimeout(() => {
          if (onComplete) {
            onComplete();
          }
        }, 2000);
      } else {
        setSuccess(true);
        setTimeout(() => {
          if (onComplete) {
            onComplete();
          }
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  if (success) {
    return (
      <motion.div
        className="mfa-setup-success"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <CheckCircle size={48} color="#10b981" />
        <h2>MFA Enabled Successfully</h2>
        <p>Multi-factor authentication has been enabled for your account.</p>
      </motion.div>
    );
  }

  console.log('MfaSetup render - step:', step, 'isLoading:', isLoading, 'error:', error, 'qrCodeDataUrl:', !!qrCodeDataUrl, 'manualEntryKey:', !!manualEntryKey);
  console.log('MfaSetup render - qrCodeDataUrl length:', qrCodeDataUrl?.length || 0, 'manualEntryKey:', manualEntryKey);

  // Force visibility check
  if (step === 'setup' && !isLoading && qrCodeDataUrl && manualEntryKey) {
    console.log('✅ MfaSetup should be fully visible with QR code and manual key');
  }

  return (
    <div className="mfa-setup-container" style={{ 
      minHeight: '400px',
      display: 'flex',
      flexDirection: 'column',
      visibility: 'visible',
      opacity: 1,
      width: '100%',
      position: 'relative',
      zIndex: 1
    }}>
      <div className="mfa-setup-header" style={{ visibility: 'visible', opacity: 1, marginBottom: '1.5rem' }}>
        <Shield size={32} />
        <h2>Enable Multi-Factor Authentication</h2>
        <p>Add an extra layer of security to your account</p>
      </div>
      
      {/* Debug: Always show something - make it very visible */}
      <div style={{ 
        padding: '1.5rem', 
        background: '#3b82f6', 
        borderRadius: '8px', 
        marginBottom: '1.5rem',
        border: '2px solid #2563eb',
        color: '#ffffff',
        fontWeight: 'bold',
        fontSize: '1rem',
        textAlign: 'center'
      }}>
        <p style={{ margin: 0 }}>
          🔵 MFA Setup Status: {isLoading ? 'Loading QR Code...' : qrCodeDataUrl ? '✅ Ready - QR Code Available' : '⏳ Initializing...'}
        </p>
        {qrCodeDataUrl && (
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', opacity: 0.9 }}>
            QR Code Length: {qrCodeDataUrl.length} bytes | Manual Key: {manualEntryKey ? 'Available' : 'Missing'}
          </p>
        )}
      </div>

      {error && (
        <motion.div
          className="error-message"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <AlertCircle size={20} />
          <span>{error}</span>
        </motion.div>
      )}

      {step === 'setup' && (
        <motion.div
          className="mfa-setup-step"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '1.5rem',
            visibility: 'visible',
            opacity: 1,
            minHeight: '300px'
          }}
        >
          <div className="mfa-instructions">
            <h3>Step 1: Scan QR Code</h3>
            <p>Use your authenticator app to scan this QR code:</p>
            <ul>
              <li>Google Authenticator</li>
              <li>Microsoft Authenticator</li>
              <li>Authy</li>
              <li>Any TOTP-compatible app</li>
            </ul>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Generating QR code...</p>
            </div>
          ) : (
            <>
              {qrCodeDataUrl ? (
                <div className="qr-code-container" style={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '1.5rem',
                  background: 'white',
                  borderRadius: '12px',
                  margin: '1rem 0',
                  border: '2px solid #3b82f6',
                  minHeight: '250px',
                  alignItems: 'center'
                }}>
                  <img 
                    src={qrCodeDataUrl} 
                    alt="MFA QR Code" 
                    className="qr-code-image"
                    style={{
                      width: '200px',
                      height: '200px',
                      display: 'block',
                      maxWidth: '100%'
                    }}
                    onError={(e) => {
                      console.error('QR Code image failed to load:', e);
                      e.currentTarget.style.border = '2px solid red';
                    }}
                    onLoad={() => {
                      console.log('✅ QR Code image loaded successfully');
                    }}
                  />
                </div>
              ) : qrCodeUrl ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#e74c3c', background: 'rgba(231, 76, 60, 0.1)', borderRadius: '8px', margin: '1rem 0' }}>
                  <p>QR code image could not be generated. Please use the manual entry key below.</p>
                </div>
              ) : (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#f59e0b', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', margin: '1rem 0' }}>
                  <p>Waiting for QR code data...</p>
                </div>
              )}

              {manualEntryKey ? (
                <div className="manual-entry">
                  <h4>Can't scan? Enter manually:</h4>
                  <div className="manual-entry-key">
                    <code>{manualEntryKey}</code>
                    <button
                      type="button"
                      className="copy-button"
                      onClick={() => copyToClipboard(manualEntryKey)}
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#e74c3c', background: 'rgba(231, 76, 60, 0.1)', borderRadius: '8px', margin: '1rem 0' }}>
                  <p>Manual entry key not available. Please check the console for errors.</p>
                </div>
              )}

              <div className="mfa-setup-actions">
                <motion.button
                  type="button"
                  className="primary-button"
                  onClick={() => setStep('verify')}
                  disabled={!qrCodeUrl && !manualEntryKey}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  I've scanned the code
                </motion.button>
                {onCancel && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={onCancel}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </>
          )}
        </motion.div>
      )}

      {step === 'verify' && (
        <motion.form
          className="mfa-verify-step"
          onSubmit={handleVerify}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mfa-instructions">
            <h3>Step 2: Verify Setup</h3>
            <p>Enter the 6-digit code from your authenticator app to complete setup:</p>
          </div>

          <div className="form-group">
            <label htmlFor="verificationCode" style={{
              display: 'block',
              marginBottom: '0.75rem',
              fontWeight: '600',
              fontSize: '1rem',
              color: '#e2e8f0',
              letterSpacing: '0.025em'
            }}>Verification Code</label>
            <input
              id="verificationCode"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              required
              disabled={isLoading}
              maxLength={6}
              pattern="[0-9]{6}"
              autoComplete="off"
              style={{
                width: '100%',
                padding: '1rem 1.25rem',
                fontSize: '1.25rem',
                fontWeight: '600',
                letterSpacing: '0.5em',
                textAlign: 'center',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: '#e2e8f0',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div className="mfa-setup-actions">
            <motion.button
              type="submit"
              className="primary-button"
              disabled={isLoading || verificationCode.length !== 6}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? (
                <>
                  <div className="button-spinner"></div>
                  Verifying...
                </>
              ) : (
                'Verify & Enable MFA'
              )}
            </motion.button>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setStep('setup')}
              disabled={isLoading}
            >
              Back
            </button>
          </div>
        </motion.form>
      )}
    </div>
  );
};

export default MfaSetup;

